import {
  MOBILE_BREAKPOINT,
  Modal,
  tokens,
  Typography,
  useSingletonMobileMediaQuery,
} from "@sunrun/experience-ui-components";
import styled from "@emotion/styled/macro";
import { useCallback } from "react";
import { Mixpanel } from "providers/mixpanel";
import {
  NEW_HOMES_CONTACT_BUTTON_LABEL,
  NEW_HOMES_PURCHASE_MODAL_SUBTITLE,
} from "constants/newHomes";
import { getCustomerAppMessagesUrl } from "checkout/util/constants";
import type { SalesRepUser } from "services/prospect";

const ContactSalesRepModal = ({
  testId,
  open,
  onClose,
  prospectId,
  offerId,
  isCustomer,
  salesRep,
  mixpanelEmailEvent,
  mixpanelPhoneEvent,
  mixpanelMessageEvent,
  isNewHomes = false,
}: {
  testId: string;
  open: boolean;
  onClose: () => void;
  prospectId: string;
  isCustomer: boolean;
  offerId?: string;
  salesRep: SalesRepUser;
  mixpanelEmailEvent: string;
  mixpanelPhoneEvent: string;
  mixpanelMessageEvent: string;
  isNewHomes?: boolean;
}) => {
  const isMobile = useSingletonMobileMediaQuery();
  const onCustomerClickedPhoneOrEmailSalesRep = useCallback(
    (formOfContact: "phone" | "email") => {
      if (isCustomer) {
        Mixpanel.track(
          formOfContact === "phone" ? mixpanelPhoneEvent : mixpanelEmailEvent,
          {
            prospectId,
            offerId: offerId ?? "",
          }
        );
      }
    },
    [isCustomer, prospectId, offerId, mixpanelEmailEvent, mixpanelPhoneEvent]
  );

  const onCustomerClickedSendMessage = useCallback(() => {
    Mixpanel.track(mixpanelMessageEvent, {
      prospectId,
      offerId: offerId ?? "",
    });
    window.open(getCustomerAppMessagesUrl(), "_blank", "noopener,noreferrer");
  }, [mixpanelMessageEvent, prospectId, offerId]);

  if (!open) {
    return null;
  }

  return (
    <RepContactModal
      testId={testId}
      title={
        isNewHomes ? NEW_HOMES_CONTACT_BUTTON_LABEL : "Contact your sales rep"
      }
      subtitle={
        isNewHomes
          ? NEW_HOMES_PURCHASE_MODAL_SUBTITLE
          : "Please reach out to know the details about the purchase options."
      }
      onClose={onClose}
      primaryButton={
        isCustomer
          ? {
              text: "Send Message",
              onClick: onCustomerClickedSendMessage,
              testId: "contact-sales-rep-modal-send-message",
            }
          : undefined
      }
      secondaryButton={
        isMobile ? undefined : { text: "Cancel", onClick: onClose }
      }
    >
      <RepContactData>
        <RepContactDataCell isFirst={true}>
          <Typography size={isMobile ? 14 : 20} color={tokens.BLACK}>
            {isNewHomes ? "Concierge" : "Sales representative"}
          </Typography>
          <Typography
            size={isMobile ? 14 : 20}
            color={tokens.TINTS_OFF_WHITE_20}
          >
            {salesRep.firstName} {salesRep.lastName}
          </Typography>
        </RepContactDataCell>
        <RepContactDataCell>
          <Typography size={isMobile ? 14 : 20} color={tokens.BLACK}>
            Phone number
          </Typography>
          <Typography
            size={isMobile ? 14 : 20}
            color={tokens.TINTS_OFF_WHITE_20}
          >
            <a
              href={`tel:${salesRep.phone}`}
              onClick={() => onCustomerClickedPhoneOrEmailSalesRep("phone")}
            >
              {salesRep.phone ?? ""}
            </a>
          </Typography>
        </RepContactDataCell>
        <RepContactDataCell>
          <Typography size={isMobile ? 14 : 20} color={tokens.BLACK}>
            Email
          </Typography>
          <Typography
            size={isMobile ? 14 : 20}
            color={tokens.TINTS_OFF_WHITE_20}
          >
            <a
              href={`mailto:${salesRep.email}`}
              onClick={() => onCustomerClickedPhoneOrEmailSalesRep("email")}
              rel="noreferrer"
            >
              {salesRep.email ?? ""}
            </a>
          </Typography>
        </RepContactDataCell>
      </RepContactData>
    </RepContactModal>
  );
};

const RepContactData = styled.div`
  display: flex;
  justify-content: space-between;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
  }
`;
const RepContactDataCell = styled.div<{ isFirst?: boolean }>`
  display: flex;
  flex-direction: column;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    border-bottom: 1px solid ${tokens.TINTS_OFF_WHITE_20};
    border-top: ${({ isFirst }) =>
      isFirst ? `1px solid ${tokens.TINTS_OFF_WHITE_20}` : "none"};
    padding: 20px 24px;
  }

  & a {
    color: ${tokens.TINTS_OFF_WHITE_20};
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 1px;
  }
`;

const RepContactModal = styled(Modal)`
  .modal-container {
    @media (max-width: ${MOBILE_BREAKPOINT}px) {
      left: 0 !important;
      right: 0 !important;
      width: 100% !important;
      top: auto !important;
      bottom: 0 !important;
      transform: none !important;
      height: 90%;
      border-radius: 8px 8px 0 0;
      overflow: hidden;
    }
    .modal-content-container {
      padding: 0px 32px 24px;
      @media (max-width: ${MOBILE_BREAKPOINT}px) {
        padding: 0px;
        border-radius: 0px;
      }
    }
    .modal-content-header {
      padding: 32px 32px 24px;
      [data-testid="modal-header-icon"] {
        width: 66px !important;
        height: 66px !important;
      }
      @media (max-width: ${MOBILE_BREAKPOINT}px) {
        padding: 8px 24px 12px;
        h1 {
          font-size: 44px !important;
          font-weight: 500 !important;
          line-height: 110%;
        }
        [data-testid="modal-header-icon"] {
          width: 26px !important;
          height: 26px !important;
          visibility: hidden !important;
        }
      }
    }
    .modal-content-footer {
      margin-top: auto;
      & > div {
        flex-direction: row;
      }
      & button {
        min-width: 228px;

        @media (max-width: ${MOBILE_BREAKPOINT}px) {
          width: 100%;
          padding: 0.5rem;
        }
      }
    }
  }
`;

export { ContactSalesRepModal };
